<template>
    <div class="sending-schemes-section">
        <v-expansion-panels flat v-model="expanded" multiple>
            <v-expansion-panel>
                <v-expansion-panel-header class="section-header">
                    <div class="section-header-content">
                        <h2 class="section-title">Схемы каскада</h2>
                    </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <div class="section-content">
                        <!-- Список схем -->
                        <div v-for="scheme in schemes" :key="scheme.id" class="scheme-card">
                            <div class="scheme-header">
                                <div class="scheme-header-left">
                                    <v-icon
                                        v-if="scheme.expanded"
                                        color="mainNew"
                                        @click="toggleScheme(scheme.id)"
                                    >
                                        mdi-chevron-up
                                    </v-icon>
                                    <v-icon
                                        v-else
                                        color="mainNew"
                                        @click="toggleScheme(scheme.id)"
                                    >
                                        mdi-chevron-down
                                    </v-icon>
                                    <BaseInputOutlined
                                        v-model="scheme.name"
                                        hide-details
                                        class="scheme-name-input"
                                        @input="updateSchemeName(scheme)"
                                    />
                                    <v-icon
                                        color="mainNew"
                                        class="ml-2"
                                        @click="editScheme(scheme.id)"
                                    >
                                        mdi-pencil
                                    </v-icon>
                                    <v-icon
                                        v-if="!isDefaultScheme(scheme.id)"
                                        color="error"
                                        class="ml-2"
                                        @click="confirmDeleteScheme(scheme.id)"
                                    >
                                        mdi-delete
                                    </v-icon>
                                </div>
                            </div>

                            <div v-if="scheme.expanded" class="scheme-content">
                                <!-- Тип схемы -->
                                <div class="scheme-type-section">
                                    <label class="section-label">Способ отправки:</label>
                                    <v-radio-group
                                        v-model="scheme.type"
                                        row
                                        dense
                                        @change="() => handleSchemeTypeChange(scheme, scheme.type)"
                                    >
                                        <v-radio
                                            label="Каскадная отправка"
                                            value="cascade"
                                            color="mainNew"
                                        ></v-radio>
                                        <v-radio
                                            label="Во все каналы сразу"
                                            value="all-channels"
                                            color="mainNew"
                                        ></v-radio>
                                    </v-radio-group>
                                </div>

                                <!-- Каскадная отправка -->
                                <div v-if="scheme.type === 'cascade'" class="cascade-scheme">
                                    <!-- Приоритеты каналов -->
                                    <div class="channels-priority">
                                        <label class="section-label">Приоритет каналов:</label>
                                        <draggable
                                            v-model="scheme.channels"
                                            :animation="200"
                                            handle=".priority-panel"
                                            @end="onPriorityDragEnd(scheme, $event)"
                                            class="priority-list"
                                            :group="{ name: 'channels', pull: true, put: true }"
                                            :sort="true"
                                            :force-fallback="true"
                                            :fallback-class="'sortable-fallback'"
                                            :ghost-class="'sortable-ghost'"
                                        >
                                            <SchemeChannelCard
                                                v-for="(channel, index) in sortedCascadeChannels(scheme)"
                                                :key="channel.id"
                                                :channel="channel"
                                                :priority="channel.priority"
                                                :is-first="index === 0"
                                                :is-last="index === sortedCascadeChannels(scheme).length - 1"
                                                :connected-channels="connectedChannels"
                                                @connect="handleChannelConnect"
                                                @move-up="moveChannelPriority(scheme, channel.id, 'up')"
                                                @move-down="moveChannelPriority(scheme, channel.id, 'down')"
                                                @delete="removeChannelFromScheme(scheme, channel.id)"
                                            />
                                        </draggable>
                                        <div class="available-channels">
                                            <label class="section-label">Доступные каналы:</label>
                                            <draggable
                                                :list="getAvailableChannelsList(scheme)"
                                                :animation="200"
                                                @add="onAvailableChannelsAdd(scheme, $event)"
                                                @remove="onAvailableChannelsRemove(scheme, $event)"
                                                class="channels-grid"
                                                :group="{ name: 'channels', pull: true, put: true }"
                                                :sort="false"
                                                :force-fallback="true"
                                                :fallback-class="'sortable-fallback'"
                                                :ghost-class="'sortable-ghost'"
                                            >
                                                <div
                                                    v-for="channel in availableChannelsForScheme(scheme)"
                                                    :key="channel.id"
                                                    class="available-channel-item"
                                                >
                                                    <ChannelCard
                                                        :channel="channel"
                                                        :connected-channels="connectedChannels"
                                                        @connect="handleChannelConnect"
                                                    />
                                                </div>
                                            </draggable>
                                        </div>
                                    </div>

                                    <!-- Режим прохождения каскада -->
                                    <div class="cascade-mode-section">
                                        <label class="section-label">Режим прохождения каскада:</label>
                                        <v-radio-group
                                            v-model="scheme.cascadeMode"
                                            row
                                            dense
                                            @change="() => handleCascadeModeChange(scheme, scheme.cascadeMode)"
                                        >
                                            <v-radio
                                                label="Быстрый"
                                                value="fast"
                                                color="mainNew"
                                            ></v-radio>
                                            <v-radio
                                                label="Аккуратный"
                                                value="accurate"
                                                color="mainNew"
                                            ></v-radio>
                                            <v-radio
                                                label="Свой"
                                                value="custom"
                                                color="mainNew"
                                            ></v-radio>
                                        </v-radio-group>
                                        <div class="mode-description">
                                            <p v-if="scheme.cascadeMode === 'fast'">
                                                В быстром режиме период между отправками по первому и последнему каналу схемы равен 1 часу
                                            </p>
                                            <p v-else-if="scheme.cascadeMode === 'accurate'">
                                                В аккуратном режиме период между отправками по первому и последнему каналу схемы равен 48 часам
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Во все каналы сразу -->
                                <div v-else-if="scheme.type === 'all-channels'" class="all-channels-scheme">
                                    <label class="section-label">Выберите каналы:</label>
                                    <div class="channels-checkboxes">
                                        <div
                                            v-for="channel in availableChannels"
                                            :key="channel.id"
                                            class="channel-checkbox-item"
                                            @click="toggleChannelSelection(scheme, channel.id)"
                                        >
                                            <v-simple-checkbox
                                                :value="isChannelSelected(scheme, channel.id)"
                                                @input="toggleChannelSelection(scheme, channel.id)"
                                                color="mainNew"
                                                :ripple="false"
                                            ></v-simple-checkbox>
                                            <div class="channel-checkbox-content">
                                                <ChannelCard
                                                    :channel="channel"
                                                    :connected-channels="connectedChannels"
                                                    :show-checkbox="false"
                                                    @connect="handleChannelConnect"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Переключатели по умолчанию -->
                                <div class="default-toggles">
                                    <div class="toggle-item">
                                        <v-switch
                                            hide-details
                                            :input-value="scheme.defaultForNotifications"
                                            @change="toggleDefault(scheme, 'notifications', $event)"
                                        ></v-switch>
                                        <span class="toggle-label">
                                            Использовать этот шаблон по умолчанию для уведомлений
                                        </span>
                                        <v-icon small color="grey" class="ml-1">mdi-help-circle-outline</v-icon>
                                    </div>
                                    <div class="toggle-item">
                                        <v-switch
                                            hide-details
                                            :input-value="scheme.defaultForMailings"
                                            @change="toggleDefault(scheme, 'mailings', $event)"
                                        ></v-switch>
                                        <span class="toggle-label">
                                            Использовать этот шаблон по умолчанию для рассылок
                                        </span>
                                        <v-icon small color="grey" class="ml-1">mdi-help-circle-outline</v-icon>
                                    </div>
                                    <div class="toggle-item">
                                        <v-switch
                                            hide-details
                                            :input-value="scheme.defaultForCrmMessages"
                                            @change="toggleDefault(scheme, 'crmMessages', $event)"
                                        ></v-switch>
                                        <span class="toggle-label">
                                            Использовать эту схему по умолчанию для сообщений из CRM
                                        </span>
                                        <v-icon small color="grey" class="ml-1">mdi-help-circle-outline</v-icon>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Кнопки добавления -->
                        <div class="add-buttons">
                            <v-btn
                                outlined
                                color="mainNew"
                                @click="$emit('add-cascade-scheme')"
                            >
                                <v-icon left>mdi-plus</v-icon>
                                Добавить схему каскадной отправки
                            </v-btn>
                            <v-btn
                                outlined
                                color="mainNew"
                                class="ml-4"
                                @click="$emit('add-all-channels-scheme')"
                            >
                                <v-icon left>mdi-plus</v-icon>
                                Добавить схему отправки во все каналы
                            </v-btn>
                        </div>
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>

<script>
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
import ChannelCard from './ChannelCard.vue'
import SchemeChannelCard from './SchemeChannelCard.vue'
import draggable from 'vuedraggable'

export default {
    name: 'SendingSchemesSection',
    components: {
        BaseInputOutlined,
        ChannelCard,
        SchemeChannelCard,
        draggable,
    },
    props: {
        schemes: {
            type: Array,
            required: true,
        },
        connectedChannels: {
            type: Array,
            required: true,
        },
    },
    data: () => ({
        expanded: [0],
        availableChannelsLists: {}, // Хранилище списков доступных каналов для каждой схемы
    }),
    computed: {
        availableChannels() {
            return this.connectedChannels
        },
    },
    methods: {
        toggleScheme(schemeId) {
            const scheme = this.schemes.find(s => s.id === schemeId)
            if (scheme) {
                if (!scheme.hasOwnProperty('expanded')) {
                    this.$set(scheme, 'expanded', false)
                }
                this.$set(scheme, 'expanded', !scheme.expanded)
                this.$emit('update-scheme', scheme)
            }
        },
        editScheme(schemeId) {
            // Редактирование схемы уже доступно через инпут названия
        },
        updateSchemeName(scheme) {
            this.$emit('update-scheme', scheme)
        },
        handleSchemeTypeChange(scheme, newType) {
            // При смене типа схемы нужно перестроить структуру каналов
            const index = this.schemes.findIndex(s => s.id === scheme.id)
            if (index === -1) return
            
            // Обновляем тип схемы
            const updatedScheme = { ...this.schemes[index], type: newType }
            if (updatedScheme.type === 'cascade') {
                // Преобразуем selected в priority
                if (!updatedScheme.cascadeMode) {
                    updatedScheme.cascadeMode = 'fast'
                }
                updatedScheme.channels = updatedScheme.channels
                    .filter(ch => ch.selected !== false)
                    .map((ch, idx) => ({
                        ...ch,
                        priority: ch.priority || idx + 1,
                        selected: undefined,
                    }))
            } else if (updatedScheme.type === 'all-channels') {
                // Преобразуем priority в selected
                // Добавляем все доступные каналы, если их еще нет в схеме
                const existingChannelIds = updatedScheme.channels.map(ch => ch.id)
                const missingChannels = this.connectedChannels.filter(ch => !existingChannelIds.includes(ch.id))
                
                // Добавляем недостающие каналы с selected: true
                missingChannels.forEach(ch => {
                    updatedScheme.channels.push({
                        ...ch,
                        selected: true,
                        priority: undefined,
                    })
                })
                
                // Обновляем существующие каналы
                updatedScheme.channels = updatedScheme.channels.map(ch => ({
                    ...ch,
                    selected: ch.selected !== undefined ? ch.selected : true,
                    priority: undefined,
                }))
            }
            // Обновляем схему в массиве
            this.$set(this.schemes, index, updatedScheme)
            // Эмитим событие для обновления имени схемы
            this.$emit('update-scheme-type', updatedScheme)
        },
        handleCascadeModeChange(scheme, newMode) {
            // Обновляем схему в массиве
            const index = this.schemes.findIndex(s => s.id === scheme.id)
            if (index === -1) return
            
            const updatedScheme = { ...this.schemes[index], cascadeMode: newMode }
            this.$set(this.schemes, index, updatedScheme)
            // Эмитим событие для обновления имени схемы при изменении режима каскада
            this.$emit('update-scheme-type', updatedScheme)
        },
        sortedCascadeChannels(scheme) {
            return [...scheme.channels].sort((a, b) => (a.priority || 0) - (b.priority || 0))
        },
        isChannelSelected(scheme, channelId) {
            return scheme.channels.some(ch => ch.id === channelId && ch.selected)
        },
        toggleChannelSelection(scheme, channelId) {
            const channelIndex = scheme.channels.findIndex(ch => ch.id === channelId)
            if (channelIndex === -1) {
                // Добавляем новый канал
                const channel = this.connectedChannels.find(ch => ch.id === channelId)
                if (channel) {
                    scheme.channels.push({
                        ...channel,
                        selected: true,
                    })
                }
            } else {
                // Переключаем выбор
                const currentValue = scheme.channels[channelIndex].selected !== false
                scheme.channels[channelIndex].selected = !currentValue
            }
            // Обновляем имя схемы при изменении выбора каналов
            this.$emit('update-scheme', scheme)
            this.$emit('update-scheme-type', scheme)
        },
        toggleDefault(scheme, type, value) {
            // Эмитим событие наверх для глобального управления
            this.$emit('toggle-default', {
                source: 'scheme',
                sourceId: scheme.id,
                type: type,
                value: value,
            })
        },
        handleChannelConnect(channel) {
            this.$emit('connect-channel', channel)
        },
        confirmDeleteScheme(schemeId) {
            if (confirm('Вы уверены, что хотите удалить эту схему?')) {
                this.$emit('delete-scheme', schemeId)
            }
        },
        isDefaultScheme(schemeId) {
            // Первые 3 схемы - стандартные
            const defaultIds = ['scheme-1', 'scheme-2', 'scheme-3']
            return defaultIds.includes(schemeId)
        },
        availableChannelsForScheme(scheme) {
            // Возвращаем каналы, которые еще не добавлены в схему
            const schemeChannelIds = scheme.channels.map(ch => ch.id)
            return this.connectedChannels.filter(ch => !schemeChannelIds.includes(ch.id))
        },
        addChannelToScheme(scheme, channel) {
            if (scheme.type === 'cascade') {
                const maxPriority = scheme.channels.length > 0
                    ? Math.max(...scheme.channels.map(ch => ch.priority || 0))
                    : 0
                scheme.channels.push({
                    ...channel,
                    priority: maxPriority + 1,
                })
            } else if (scheme.type === 'all-channels') {
                scheme.channels.push({
                    ...channel,
                    selected: true,
                })
            }
            this.$emit('update-scheme', scheme)
        },
        removeChannelFromScheme(scheme, channelId) {
            const index = scheme.channels.findIndex(ch => ch.id === channelId)
            if (index !== -1) {
                scheme.channels.splice(index, 1)
                // Пересчитываем приоритеты для каскадных схем
                if (scheme.type === 'cascade') {
                    scheme.channels.forEach((ch, idx) => {
                        ch.priority = idx + 1
                    })
                }
                this.$emit('update-scheme', scheme)
            }
        },
        moveChannelPriority(scheme, channelId, direction) {
            const sorted = this.sortedCascadeChannels(scheme)
            const index = sorted.findIndex(ch => ch.id === channelId)
            if (index === -1) return

            const newIndex = direction === 'up' ? index - 1 : index + 1
            if (newIndex < 0 || newIndex >= sorted.length) return

            // Меняем приоритеты
            const currentPriority = sorted[index].priority
            const targetPriority = sorted[newIndex].priority

            sorted[index].priority = targetPriority
            sorted[newIndex].priority = currentPriority

            // Обновляем массив каналов в схеме
            scheme.channels = sorted
            this.$emit('update-scheme', scheme)
        },
        getAvailableChannelsList(scheme) {
            // Создаем или возвращаем реактивный список доступных каналов для draggable
            if (!this.availableChannelsLists[scheme.id]) {
                this.$set(this.availableChannelsLists, scheme.id, [])
            }
            // Синхронизируем список с доступными каналами
            const currentIds = this.availableChannelsLists[scheme.id].map(ch => ch.id)
            const available = this.availableChannelsForScheme(scheme)
            const newIds = available.map(ch => ch.id)
            
            // Обновляем список только если изменился состав
            if (JSON.stringify(currentIds.sort()) !== JSON.stringify(newIds.sort())) {
                this.$set(this.availableChannelsLists, scheme.id, [...available])
            }
            
            return this.availableChannelsLists[scheme.id]
        },
        onPriorityDragEnd(scheme, evt) {
            // Обновляем приоритеты после перетаскивания
            if (evt.oldIndex !== evt.newIndex && evt.newIndex !== undefined) {
                scheme.channels.forEach((ch, index) => {
                    ch.priority = index + 1
                })
                this.$emit('update-scheme', scheme)
            }
        },
        onAvailableChannelsAdd(scheme, evt) {
            // Канал был добавлен в схему из доступных (это событие срабатывает на списке доступных каналов)
            // На самом деле канал уже добавлен в scheme.channels через draggable
            this.$nextTick(() => {
                // Пересчитываем приоритеты
                if (scheme.type === 'cascade') {
                    scheme.channels.forEach((ch, idx) => {
                        ch.priority = idx + 1
                    })
                } else if (scheme.type === 'all-channels') {
                    scheme.channels.forEach(ch => {
                        if (ch.selected === undefined) {
                            ch.selected = true
                        }
                    })
                }
                // Обновляем список доступных каналов
                this.$set(this.availableChannelsLists, scheme.id, [...this.availableChannelsForScheme(scheme)])
                this.$emit('update-scheme', scheme)
            })
        },
        onAvailableChannelsRemove(scheme, evt) {
            // Канал был удален из доступных (перетащен в схему)
            // Канал уже добавлен в scheme.channels через draggable
            this.$nextTick(() => {
                // Пересчитываем приоритеты
                if (scheme.type === 'cascade') {
                    scheme.channels.forEach((ch, idx) => {
                        ch.priority = idx + 1
                    })
                }
                // Обновляем список доступных каналов
                this.$set(this.availableChannelsLists, scheme.id, [...this.availableChannelsForScheme(scheme)])
                this.$emit('update-scheme', scheme)
            })
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.sending-schemes-section
    background: white
    border-radius: 8px
    padding: 16px

.section-header
    padding: 0 !important

.section-header-content
    display: flex
    justify-content: space-between
    align-items: center
    width: 100%

.section-title
    font-size: 18px
    font-weight: 500
    margin: 0

.section-content
    padding: 24px 0

.scheme-card
    margin-bottom: 24px
    border: 1px solid #E0E0E0
    border-radius: 8px
    padding: 16px

.scheme-header
    display: flex
    justify-content: space-between
    align-items: center
    margin-bottom: 16px

.scheme-header-left
    display: flex
    align-items: center
    flex: 1

.scheme-name-input
    flex: 1
    margin-left: 8px

.scheme-content
    padding-top: 16px
    border-top: 1px solid #E0E0E0

.section-label
    font-size: 14px
    font-weight: 500
    color: #2A3B51
    margin-bottom: 12px
    display: block

.scheme-type-section
    margin-bottom: 24px

.channels-priority
    margin-bottom: 24px

.priority-list
    display: flex
    flex-direction: column
    gap: 0
    margin-top: 12px
    user-select: none
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none

::v-deep .sortable-ghost
    opacity: 0.4
    background: #f0f0f0
    user-select: none
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none

::v-deep .sortable-fallback
    opacity: 0.8
    user-select: none
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none

.cascade-mode-section
    margin-bottom: 24px

.mode-description
    margin-top: 8px
    font-size: 12px
    color: #7F8997

.channels-checkboxes
    display: flex
    flex-direction: column
    gap: 12px
    margin-top: 12px

.channel-checkbox-item
    display: flex
    align-items: center
    gap: 12px
    cursor: pointer

.channel-checkbox-content
    flex: 1

.available-channels
    margin-top: 24px
    padding-top: 24px
    border-top: 1px solid #E0E0E0

.channels-grid
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr))
    gap: 16px
    margin-top: 12px
    min-height: 100px
    padding: 12px
    border: 2px dashed #E0E0E0
    border-radius: 8px
    background: #FAFAFA
    user-select: none
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none
    -webkit-touch-callout: none

.available-channel-item
    display: flex
    flex-direction: column
    gap: 8px
    user-select: none
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none
    -webkit-touch-callout: none
    cursor: grab
    &:active
        cursor: grabbing

::v-deep .sortable-ghost
    opacity: 0.4
    background: #f0f0f0
    user-select: none
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none
    -webkit-touch-callout: none

::v-deep .sortable-fallback
    opacity: 0.8
    user-select: none
    -webkit-user-select: none
    -moz-user-select: none
    -ms-user-select: none
    -webkit-touch-callout: none

.default-toggles
    margin-top: 24px
    padding-top: 24px
    border-top: 1px solid #E0E0E0

.toggle-item
    display: flex
    align-items: center
    margin-bottom: 16px

.toggle-label
    margin-left: 12px
    font-size: 14px
    color: #2A3B51

.add-buttons
    margin-top: 24px
    display: flex
    gap: 16px
</style>

